/**
 * QMS Pattern Card
 * Displays details of a single detected pattern with actionable recommendations
 */

import {
  AlertTriangle,
  MapPin,
  Building2,
  TrendingUp,
  Calendar,
  FileText,
  ListChecks,
  Clock3,
  ClipboardCheck,
  ShieldCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import type {
  QmsPattern,
  QmsPatternSeverity,
  QmsRepeatIssuePattern,
  QmsLocationHotspotPattern,
  QmsDepartmentSpikePattern,
  QmsSlaBreachClusterPattern,
  QmsCapaOverduePattern,
  QmsApprovalBottleneckPattern,
} from '../../types/qms.types';
import {
  formatQmsCapaAction,
  formatQmsDepartmentName,
  formatQmsTrendSlaStage,
  getDepartmentSpikeSummary,
} from './qmsTrendPresentation';
import {
  isRepeatIssuePattern,
  isLocationHotspotPattern,
  isDepartmentSpikePattern,
  isSlaBreachClusterPattern,
  isCapaOverduePattern,
  isApprovalBottleneckPattern,
} from '../../types/qms.types';

const SEVERITY_STYLES: Record<QmsPatternSeverity, { border: string; bg: string; text: string; badge: string }> = {
  critical: {
    border: 'border-red-300',
    bg: 'bg-red-50',
    text: 'text-red-900',
    badge: 'bg-red-600 text-white',
  },
  high: {
    border: 'border-orange-300',
    bg: 'bg-orange-50',
    text: 'text-orange-900',
    badge: 'bg-orange-600 text-white',
  },
  medium: {
    border: 'border-yellow-300',
    bg: 'bg-yellow-50',
    text: 'text-yellow-900',
    badge: 'bg-yellow-600 text-white',
  },
  low: {
    border: 'border-blue-300',
    bg: 'bg-blue-50',
    text: 'text-blue-900',
    badge: 'bg-blue-600 text-white',
  },
};

const PATTERN_TYPE_LABELS = {
  repeat_issue: 'Repeat Issue',
  location_hotspot: 'Location Hotspot',
  department_spike: 'Department Spike',
  sla_breach_cluster: 'SLA Breach Cluster',
  capa_overdue: 'CAPA Overdue',
  approval_bottleneck: 'Approval Bottleneck',
};

const PATTERN_TYPE_ICONS = {
  repeat_issue: AlertTriangle,
  location_hotspot: MapPin,
  department_spike: TrendingUp,
  sla_breach_cluster: Clock3,
  capa_overdue: ClipboardCheck,
  approval_bottleneck: ShieldCheck,
};

interface QmsPatternCardProps {
  pattern: QmsPattern;
  onViewTickets?: (ticketIds: string[]) => void;
  onOpenDepartment?: (department: string) => void;
}

export function QmsPatternCard({ pattern, onViewTickets, onOpenDepartment }: QmsPatternCardProps) {
  const styles = SEVERITY_STYLES[pattern.severity];
  const Icon = PATTERN_TYPE_ICONS[pattern.pattern_type];

  const renderPatternDetails = () => {
    if (isRepeatIssuePattern(pattern)) {
      return (
        <RepeatIssueDetails pattern={pattern} onViewTickets={onViewTickets} />
      );
    }

    if (isLocationHotspotPattern(pattern)) {
      return (
        <LocationHotspotDetails pattern={pattern} onViewTickets={onViewTickets} />
      );
    }

    if (isDepartmentSpikePattern(pattern)) {
      return (
        <DepartmentSpikeDetails pattern={pattern} onOpenDepartment={onOpenDepartment} />
      );
    }

    if (isSlaBreachClusterPattern(pattern)) {
      return (
        <SlaBreachClusterDetails pattern={pattern} onViewTickets={onViewTickets} />
      );
    }

    if (isCapaOverduePattern(pattern)) {
      return (
        <CapaOverdueDetails pattern={pattern} onViewTickets={onViewTickets} />
      );
    }

    if (isApprovalBottleneckPattern(pattern)) {
      return (
        <ApprovalBottleneckDetails pattern={pattern} onViewTickets={onViewTickets} />
      );
    }

    return null;
  };

  return (
    <div className={`rounded-lg border-2 ${styles.border} ${styles.bg} p-4 shadow-sm`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${styles.text}`} />
          <h4 className={`text-sm font-semibold ${styles.text}`}>
            {PATTERN_TYPE_LABELS[pattern.pattern_type]}
          </h4>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles.badge}`}>
          {pattern.severity.toUpperCase()}
        </span>
      </div>

      {/* Pattern-specific details */}
      {renderPatternDetails()}

      {/* Recommendation */}
      <div className="mt-3 pt-3 border-t border-current/10">
        <div className="flex items-start gap-2">
          <FileText className={`h-4 w-4 ${styles.text} mt-0.5 flex-shrink-0`} />
          <div>
            <p className="text-xs font-medium text-gray-700 mb-1">Recommendation:</p>
            <p className={`text-sm ${styles.text}`}>{pattern.recommendation}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sub-components for pattern-specific details

function RepeatIssueDetails({
  pattern,
  onViewTickets,
}: {
  pattern: QmsRepeatIssuePattern;
  onViewTickets?: (ticketIds: string[]) => void;
}) {
  const timespanText = pattern.timespan_days > 0
    ? `over ${Math.round(pattern.timespan_days)} days`
    : 'on the same day';

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <Building2 className="h-4 w-4 text-gray-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{pattern.site_name}</p>
          <p className="text-xs text-gray-600 mt-0.5">Site</p>
        </div>
      </div>

      <div className="bg-white/50 rounded-md p-2">
        <p className="text-sm font-medium text-gray-900 mb-1">Root Cause:</p>
        <p className="text-sm text-gray-700">{pattern.cause}</p>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-600">
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          <span>{pattern.count} occurrences {timespanText}</span>
        </div>
        {onViewTickets && pattern.ticket_ids.length > 0 && (
          <button
            type="button"
            onClick={() => onViewTickets(pattern.ticket_ids)}
            className="text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Review {pattern.ticket_ids.length} ticket{pattern.ticket_ids.length === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {pattern.first_occurrence && pattern.last_occurrence && (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Calendar className="h-3 w-3" />
          <span>
            {format(new Date(pattern.first_occurrence), 'MMM d')} -{' '}
            {format(new Date(pattern.last_occurrence), 'MMM d, yyyy')}
          </span>
        </div>
      )}
    </div>
  );
}

function LocationHotspotDetails({
  pattern,
  onViewTickets,
}: {
  pattern: QmsLocationHotspotPattern;
  onViewTickets?: (ticketIds: string[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <MapPin className="h-4 w-4 text-gray-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{pattern.location_name}</p>
          <p className="text-xs text-gray-600 mt-0.5">Location</p>
        </div>
      </div>

      <div className="bg-white/50 rounded-md p-2">
        <p className="text-sm font-medium text-gray-900">
          {pattern.count} quality issues detected at this location
        </p>
        <p className="text-xs text-gray-600 mt-1">
          within the past {Math.round(pattern.timespan_days)} days
        </p>
      </div>

      {onViewTickets && pattern.ticket_ids.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onViewTickets(pattern.ticket_ids)}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Review {pattern.ticket_ids.length} ticket{pattern.ticket_ids.length === 1 ? '' : 's'}
          </button>
        </div>
      )}
    </div>
  );
}

function DepartmentSpikeDetails({
  pattern,
  onOpenDepartment,
}: {
  pattern: QmsDepartmentSpikePattern;
  onOpenDepartment?: (department: string) => void;
}) {
  const summary = getDepartmentSpikeSummary(pattern);
  const departmentLabel = formatQmsDepartmentName(pattern.department);

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <TrendingUp className="h-4 w-4 text-gray-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{departmentLabel} Department</p>
          <p className="text-xs font-semibold text-gray-700 mt-0.5">{summary.headline}</p>
          <p className="text-xs text-gray-600 mt-0.5">{summary.detail}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white/50 rounded-md p-2">
          <p className="text-xs text-gray-600">Previous Period</p>
          <p className="text-lg font-bold text-gray-900">{pattern.previous_count}</p>
        </div>
        <div className="bg-white/50 rounded-md p-2">
          <p className="text-xs text-gray-600">Current Period</p>
          <p className="text-lg font-bold text-gray-900">{pattern.current_count}</p>
        </div>
      </div>

      <p className="text-xs text-gray-600">
        Analysis window: Last {pattern.timespan_days} days vs previous {pattern.timespan_days} days
      </p>

      {onOpenDepartment && (
        <button
          type="button"
          onClick={() => onOpenDepartment(pattern.department)}
          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900"
        >
          <ListChecks className="h-3.5 w-3.5" />
          Review {departmentLabel} tickets
        </button>
      )}
    </div>
  );
}

function ReviewTicketsButton({
  ticketIds,
  onViewTickets,
  className = 'text-xs text-indigo-600 hover:text-indigo-800 font-medium',
}: {
  ticketIds: string[];
  onViewTickets?: (ticketIds: string[]) => void;
  className?: string;
}) {
  if (!onViewTickets || ticketIds.length === 0) return null;

  return (
    <button
      type="button"
      onClick={() => onViewTickets(ticketIds)}
      className={className}
    >
      Review {ticketIds.length} ticket{ticketIds.length === 1 ? '' : 's'}
    </button>
  );
}

function SlaBreachClusterDetails({
  pattern,
  onViewTickets,
}: {
  pattern: QmsSlaBreachClusterPattern;
  onViewTickets?: (ticketIds: string[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <Clock3 className="h-4 w-4 text-gray-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{pattern.group_name}</p>
          <p className="text-xs text-gray-600 mt-0.5">
            {formatQmsTrendSlaStage(pattern.stage)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white/50 rounded-md p-2">
          <p className="text-xs text-gray-600">Tickets</p>
          <p className="text-lg font-bold text-gray-900">{pattern.count}</p>
        </div>
        <div className="bg-white/50 rounded-md p-2">
          <p className="text-xs text-gray-600">Overdue</p>
          <p className="text-lg font-bold text-gray-900">{pattern.overdue_count}</p>
        </div>
        <div className="bg-white/50 rounded-md p-2">
          <p className="text-xs text-gray-600">Warning</p>
          <p className="text-lg font-bold text-gray-900">{pattern.warning_count}</p>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-600">
        <span>Max overdue: {pattern.max_hours_overdue}h</span>
        <ReviewTicketsButton ticketIds={pattern.ticket_ids} onViewTickets={onViewTickets} />
      </div>
    </div>
  );
}

function CapaOverdueDetails({
  pattern,
  onViewTickets,
}: {
  pattern: QmsCapaOverduePattern;
  onViewTickets?: (ticketIds: string[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <ClipboardCheck className="h-4 w-4 text-gray-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {formatQmsCapaAction(pattern.action_type)}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            {pattern.count} overdue CAPA deadline{pattern.count === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <div className="bg-white/50 rounded-md p-2">
        <p className="text-sm font-medium text-gray-900">
          Oldest due date: {format(new Date(pattern.oldest_due_at), 'MMM d, yyyy')}
        </p>
        <p className="text-xs text-gray-600 mt-1">
          Max overdue: {pattern.max_days_overdue} day{pattern.max_days_overdue === 1 ? '' : 's'}
        </p>
      </div>

      <div className="flex justify-end">
        <ReviewTicketsButton ticketIds={pattern.ticket_ids} onViewTickets={onViewTickets} />
      </div>
    </div>
  );
}

function ApprovalBottleneckDetails({
  pattern,
  onViewTickets,
}: {
  pattern: QmsApprovalBottleneckPattern;
  onViewTickets?: (ticketIds: string[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 text-gray-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {formatQmsTrendSlaStage(pattern.approval_stage)}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            {pattern.count} ticket{pattern.count === 1 ? '' : 's'} waiting for approval
          </p>
        </div>
      </div>

      <div className="bg-white/50 rounded-md p-2">
        <p className="text-sm font-medium text-gray-900">
          Oldest wait: {pattern.oldest_age_days} day{pattern.oldest_age_days === 1 ? '' : 's'}
        </p>
        <p className="text-xs text-gray-600 mt-1">
          Max severity level: {pattern.max_severity}
        </p>
      </div>

      <div className="flex justify-end">
        <ReviewTicketsButton ticketIds={pattern.ticket_ids} onViewTickets={onViewTickets} />
      </div>
    </div>
  );
}
